import type { ReactNode } from 'react';

import {
  AbsoluteBackgroundStack,
  resolveBackgroundConfig,
} from '@/components/shared/background-settings/absolute-stack';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useEffectiveColors } from '@/hooks/use-public-design-settings';
import {
  buildCardStyle,
  findCardTemplateBlock,
  getAlternatingContainerClassName,
  getCardBgClassName,
  getCollectionCardPaddingClassName,
  getCollectionContainerClassName,
  isCollectionBlock,
  resolveCardConfigFromTemplate,
  resolveSymmetricAlternatingGap,
  resolveSymmetricCarouselDisplay,
  resolveSymmetricCollectionProps,
  resolveSymmetricGridLayout,
  resolveSymmetricPadding,
} from '@/lib/blocks/collection/blocks';
import {
  isDataWidgetBlock,
  resolveDataWidgetCardConfig,
} from '@/lib/blocks/collection/data-widget/blocks';
import {
  getIndexCollectionVariantProps,
  isIndexCollectionBlock,
  resolveIndexCollectionProps,
} from '@/lib/blocks/collection/index-collection/blocks';
import { parseCollectionMediaLayout } from '@/lib/blocks/collection/media-layout';
import { getVariantEntry } from '@/lib/blocks/collection/variant-registry';
import {
  blockOrDescendantsForceFullWidth,
  getBlockDisplayWidthStyle,
} from '@/lib/blocks/layout/block-layout';
import {
  type BlockStackLayoutConfig,
  getBlockStackContainerClassName,
  getBlockStackTextAlignClassName,
  getTopLevelBlockWrapperClassName,
  shouldStretchTopLevelBlockWrapper,
} from '@/lib/blocks/layout/block-stack-layout';
import {
  GROUP_MEDIA_SHELL_CLASSNAME,
  getGroupChildClassName,
  getGroupContainerClassName,
  getGroupCustomStyle,
  getGroupCustomStyleClassName,
  getGroupSurfaceClassName,
  hasGroupMediaBackground,
  isBlockFloated,
  resolveGroupLayout,
  shouldStretchGroupToFillParent,
  shouldUseBlockFlowForAncestorFloat,
} from '@/lib/blocks/layout/group-layout';
import { buildBlockTree } from '@/lib/blocks/tree';
import { SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES } from '@/lib/derive-content-tone';
import { readImageValue } from '@/lib/prop-utils';
import { isSlotPlaceholderContent, pairedLabelSlot } from '@/lib/slot-placeholders';
import { cn } from '@/lib/utils';
import type { PageBlock } from '@/types/block';
import { getImageUrl, isPendingAIImage } from '@/types/image';
import { BlockRenderer } from './block-renderer';
import {
  AccordionCollectionRenderer,
  AlternatingCollectionCardRenderer,
  CollectionCardRenderer,
  CollectionCarouselRenderer,
} from './collection-card-renderer';
import { DataWidgetRenderer } from './data-widget-renderer';
import { IndexCollectionRenderer } from './index-collection-renderer';

interface SectionGridRendererProps {
  blocks: PageBlock[];
  showHidden?: boolean;
  stackLayout?: BlockStackLayoutConfig;
  interactive?: boolean;
  wrapBlock?: (block: PageBlock, content: ReactNode) => ReactNode;
  wrapParent?: (block: PageBlock, content: ReactNode) => ReactNode;
}

export function SectionGridRenderer({
  blocks,
  showHidden = false,
  stackLayout,
  interactive = true,
  wrapBlock,
  wrapParent,
}: SectionGridRendererProps) {
  const cardColors = useEffectiveColors();
  const isPageEditor = useIsPageEditor();
  const visibleBlocks = blocks.filter((block) => showHidden || block.visible);
  const hiddenLabelSlots = new Set<string>();
  for (const block of visibleBlocks) {
    const slot = block.props?.slot;
    if (typeof slot !== 'string') continue;
    if (!isSlotPlaceholderContent(slot, block.props?.content)) continue;
    const labelSlot = pairedLabelSlot(slot);
    if (labelSlot) hiddenLabelSlots.add(labelSlot);
  }
  const cardTemplateIds = new Set<string>();
  for (const block of visibleBlocks) {
    if (block.blockType === 'group' && block.props?.role === 'card_template') {
      cardTemplateIds.add(block.id);
    }
  }
  const emptyImageParentIds = new Set<string>();
  const renderableBlocks = visibleBlocks.filter((block) => {
    if (
      !isPageEditor &&
      block.blockType === 'image' &&
      !(block.parentBlockId && cardTemplateIds.has(block.parentBlockId))
    ) {
      const image = readImageValue(block.props?.image);
      if (!getImageUrl(image) && !isPendingAIImage(image)) {
        if (block.parentBlockId) emptyImageParentIds.add(block.parentBlockId);
        return false;
      }
    }
    const slot = block.props?.slot;
    if (typeof slot !== 'string') return true;
    if (isSlotPlaceholderContent(slot, block.props?.content)) return false;
    return !hiddenLabelSlots.has(slot);
  });
  if (renderableBlocks.length === 0) {
    return null;
  }

  const { topLevel, childrenByParent } = buildBlockTree(renderableBlocks);
  const shouldStretchGroup = (layout: ReturnType<typeof resolveGroupLayout>, depth: number) =>
    depth === 0 && shouldStretchGroupToFillParent(layout);

  const renderGroupBranch = (
    block: PageBlock,
    depth: number,
    participatesInAncestorFloatFlow: boolean,
    renderChild: (
      child: PageBlock,
      childDepth: number,
      participatesInFloatFlow: boolean,
    ) => ReactNode,
  ): ReactNode => {
    const children = childrenByParent.get(block.id) || [];
    if (children.length === 0) {
      return null;
    }

    const layout = resolveGroupLayout(block, children);
    const stretch = Boolean(block.props?.stretch);
    const blockFlowForAncestorFloat = shouldUseBlockFlowForAncestorFloat(
      block,
      children,
      layout,
      participatesInAncestorFloatFlow,
    );
    const renderedChildren: Array<{ child: PageBlock; content: ReactNode }> = [];
    let hasPriorRenderedFloat = blockFlowForAncestorFloat;
    for (const child of children) {
      const content = renderChild(child, depth + 1, hasPriorRenderedFloat);
      if (!content) continue;

      renderedChildren.push({ child, content });
      hasPriorRenderedFloat ||= isBlockFloated(child);
    }

    if (renderedChildren.length === 0) {
      return null;
    }

    const mediaBackground = hasGroupMediaBackground(layout);
    const backgroundConfig = mediaBackground
      ? resolveBackgroundConfig(block.props ?? {}, cardColors?.siteText)
      : null;
    const soloAfterHorizontalDrop =
      layout.direction === 'horizontal' &&
      renderedChildren.length === 1 &&
      emptyImageParentIds.has(block.id);

    const container = (
      <div
        className={cn(
          getGroupContainerClassName(layout, { blockFlowForAncestorFloat }),
          getGroupSurfaceClassName(layout),
          getGroupCustomStyleClassName(layout),
          !mediaBackground &&
            layout.backgroundColor &&
            getCardBgClassName({ backgroundColor: layout.backgroundColor }, cardColors),
          mediaBackground && 'relative z-10',
          (shouldStretchGroup(layout, depth) || stretch) && 'h-full flex-1',
        )}
        style={getGroupCustomStyle(layout)}
      >
        {renderedChildren.map(({ child, content }) => (
          <div
            key={child.id}
            data-block-id={child.stableId}
            className={cn(
              getGroupChildClassName(child, layout, { blockFlowForAncestorFloat }),
              soloAfterHorizontalDrop && 'md:mx-auto',
            )}
            style={getBlockDisplayWidthStyle(child)}
          >
            {content}
          </div>
        ))}
      </div>
    );

    if (backgroundConfig) {
      return (
        <div
          className={cn(
            GROUP_MEDIA_SHELL_CLASSNAME,
            SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES[backgroundConfig.contentTone],
            (shouldStretchGroup(layout, depth) || stretch) && 'h-full flex-1',
          )}
        >
          <AbsoluteBackgroundStack
            config={backgroundConfig}
            focusX={layout.backgroundFocusX}
            focusY={layout.backgroundFocusY}
            fixed={layout.backgroundFixed && backgroundConfig.mode === 'image'}
          />
          {container}
        </div>
      );
    }

    return container;
  };

  const isHorizontalStack = stackLayout?.direction === 'horizontal';
  const topLevelTextAlignClassName = getBlockStackTextAlignClassName(stackLayout);
  const shouldStretchStackContainer = topLevel.some((block) =>
    shouldStretchTopLevelBlockWrapper({
      block,
      groupChildren: childrenByParent.get(block.id) ?? [],
      topLevelCount: topLevel.length,
      isHorizontalStack,
    }),
  );

  const renderBlock = (
    block: PageBlock,
    depth = 0,
    participatesInAncestorFloatFlow = false,
  ): ReactNode => {
    const wrap = (content: ReactNode) => (wrapParent ? wrapParent(block, content) : content);

    if (isIndexCollectionBlock(block)) {
      const cardTemplateBlock = findCardTemplateBlock(childrenByParent.get(block.id) || []);
      if (!cardTemplateBlock) return null;

      const props = resolveIndexCollectionProps(block);
      const cardConfig = resolveCardConfigFromTemplate(
        cardTemplateBlock,
        childrenByParent.get(cardTemplateBlock.id) || [],
        getIndexCollectionVariantProps(props),
      );
      if (cardConfig.fields.length === 0) return null;

      return wrap(<IndexCollectionRenderer block={block} cardConfig={cardConfig} />);
    }

    if (isDataWidgetBlock(block)) {
      const { cardConfig } = resolveDataWidgetCardConfig(block, childrenByParent);
      if (cardConfig.fields.length === 0) return null;

      return wrap(<DataWidgetRenderer block={block} cardConfig={cardConfig} />);
    }

    if (isCollectionBlock(block)) {
      const symProps = resolveSymmetricCollectionProps(block);
      if (symProps.items.length === 0) return null;

      const templateBlock = findCardTemplateBlock(childrenByParent.get(block.id) || []);

      if (!templateBlock) return null;
      const templateChildren = childrenByParent.get(templateBlock.id) || [];

      const variantEntry = getVariantEntry(symProps.variant);
      const variantSpecific =
        symProps.variantProps[symProps.variant] ?? variantEntry?.defaultProps ?? {};

      const cardConfig = resolveCardConfigFromTemplate(
        templateBlock,
        templateChildren,
        variantSpecific,
      );
      if (!cardConfig.fields.some((f) => f.enabled)) return null;
      const padding = resolveSymmetricPadding(variantSpecific);
      const mediaLayout = parseCollectionMediaLayout(variantSpecific);

      if (variantEntry?.renderer === 'carousel') {
        const display = resolveSymmetricCarouselDisplay(variantSpecific);
        const { style: cardCustomStyle, hasCardStyling } = buildCardStyle(variantSpecific);
        const cardPaddingClass = hasCardStyling ? getCollectionCardPaddingClassName(padding) : '';

        return wrap(
          <CollectionCarouselRenderer
            items={symProps.items}
            display={display}
            cardConfig={cardConfig}
            mediaLayout={mediaLayout}
            paddingClassName={cardPaddingClass}
            cardStyle={cardCustomStyle}
            hasCardStyling={hasCardStyling}
          />,
        );
      }

      if (variantEntry?.renderer === 'accordion') {
        const accordionLayout = resolveSymmetricGridLayout(variantSpecific);
        const { style: cardCustomStyle, hasCardStyling } = buildCardStyle(variantSpecific);
        const content = (
          <AccordionCollectionRenderer
            items={symProps.items}
            cardConfig={cardConfig}
            padding={padding}
            containerStyle={cardCustomStyle}
            expandFirstItem={Boolean(variantSpecific.expandFirstItem)}
            allowMultiple={variantSpecific.allowMultiple !== false}
            showDividers={variantSpecific.showDividers !== false}
            iconStyle={variantSpecific.iconStyle === 'plus' ? 'plus' : 'chevron'}
            buttonVariant={variantSpecific.buttonVariant === 'plain' ? 'plain' : 'filled'}
            layout={accordionLayout}
            hasCardStyling={hasCardStyling}
          />
        );
        return wrap(content);
      }

      if (variantEntry?.renderer === 'alternating') {
        const altGap = resolveSymmetricAlternatingGap(variantSpecific);
        const { style: cardCustomStyle, hasCardStyling } = buildCardStyle(variantSpecific);
        const cardBgClassName = getCardBgClassName(cardCustomStyle, cardColors);
        const cardPaddingClass = hasCardStyling ? getCollectionCardPaddingClassName(padding) : '';
        const content = (
          <div className={getAlternatingContainerClassName(altGap)}>
            {symProps.items.map((item, index) => (
              <div
                key={item.id}
                className={cn(hasCardStyling && 'rounded-lg', cardBgClassName)}
                style={cardCustomStyle}
              >
                <AlternatingCollectionCardRenderer
                  item={item}
                  cardConfig={cardConfig}
                  alternatingRowIndex={index}
                  mediaLayout={mediaLayout}
                  paddingClassName={cardPaddingClass}
                />
              </div>
            ))}
          </div>
        );
        return wrap(content);
      }

      const layout = resolveSymmetricGridLayout(variantSpecific);
      const { style: cardCustomStyle, hasCardStyling } = buildCardStyle(variantSpecific);
      const cardBgClassName = getCardBgClassName(cardCustomStyle, cardColors);
      const content = (
        <div className={getCollectionContainerClassName(layout)}>
          {symProps.items.map((item) => (
            <div
              key={item.id}
              className={cn(
                'h-full min-w-0',
                hasCardStyling && 'rounded-lg',
                hasCardStyling && getCollectionCardPaddingClassName(padding),
                cardBgClassName,
              )}
              style={cardCustomStyle}
            >
              <CollectionCardRenderer
                item={item}
                cardConfig={cardConfig}
                mediaLayout={mediaLayout}
              />
            </div>
          ))}
        </div>
      );
      return wrap(content);
    }

    if (block.blockType === 'group') {
      const content = renderGroupBranch(
        block,
        depth,
        participatesInAncestorFloatFlow,
        (child, childDepth, participatesInFloatFlow) =>
          renderBlock(child, childDepth, participatesInFloatFlow),
      );

      return wrap(content);
    }

    const content = <BlockRenderer block={block} interactive={interactive} />;
    return wrapBlock ? wrapBlock(block, content) : content;
  };
  return (
    <div
      className={cn(
        getBlockStackContainerClassName(stackLayout),
        shouldStretchStackContainer && 'h-full flex-1',
      )}
    >
      {topLevel.map((block, index) => {
        const content = renderBlock(block, 0);
        if (!content) {
          return null;
        }

        return (
          <div
            key={block.id}
            data-block-id={block.stableId}
            className={getTopLevelBlockWrapperClassName({
              block,
              groupChildren: childrenByParent.get(block.id) ?? [],
              topLevelCount: topLevel.length,
              isHorizontalStack,
              stackAlign: stackLayout?.align,
              stackPadding: stackLayout?.padding,
              topLevelTextAlignClassName,
              isLastBlock: index === topLevel.length - 1,
              hasForceFullWidthDescendant: blockOrDescendantsForceFullWidth(
                block,
                (b) => childrenByParent.get(b.id) || [],
              ),
            })}
            style={getBlockDisplayWidthStyle(block)}
          >
            {content}
          </div>
        );
      })}
    </div>
  );
}
